import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PageResponse } from './page-response';
import { FreightForwarderBidDto } from './freight-forwarder-bid';

const API_URL = '/cargo/api/deals';

// Интерфейсы для детального просмотра сделки (с полными объектами)
export interface Country {
  id: number;
  nameRu: string;
  nameEn: string;
  code: string;
}

export interface CargoType {
  id: number;
  nameRu: string;
  nameEn: string;
}

export interface CargoRequestProduct {
  id?: number;
  cargoType?: CargoType;
  cargoTypeId?: number;
  name?: string;
  tnvedCode?: string;
  isDangerous: boolean;
  isOversized: boolean;
  totalWeightKg: number;
  lengthMm?: number;
  widthMm?: number;
  heightMm?: number;
  volumeM3?: number;
  isNonStackable: boolean;
}

export interface CargoRequestDetail {
  id: number;
  cargoOwnerId?: number;
  cargoOwnerCompanyName?: string;
  rateType: 'FTL' | 'LTL' | 'AIR' | 'CONTAINER';
  fromCountry?: Country;
  fromCity?: string;
  toCountry?: Country;
  toCity?: string;
  loadingDate?: string;
  price?: number;
  currencyId?: number;
  currency?: any;
  calculateCustomsPayments?: boolean;
  products: CargoRequestProduct[];
  createdAt?: string;
  updatedAt?: string;
  bidsCount?: number;
  hasDeal?: boolean;
}

export interface ParticipantInfo {
  id: number;
  companyName: string;
  fullName: string;
  email: string;
  phone?: string;
}

export interface Role {
  id: number;
  name: string;
  code: string;
}

export interface DealActivityType {
  id: number;
  code: string;
  nameRu: string;
  nameEn: string;
  category?: 'DOCUMENT' | 'STATUS' | 'REQUEST';
}

export interface DealActivity {
  id: number;
  dealId: number;
  activityType: DealActivityType;
  actorRole: Role;
  targetRole?: Role;
  actorMessage?: string;
  targetMessage?: string;
  request: boolean;
  completed: boolean;
  createdAt: string;
  updatedAt?: string;
  createdByUserId: number;
  updatedByUserId?: number;
}

/** Фильтр списка сделок по ответственным (действует у администратора компании). */
export interface DealAssigneeFilter {
  managerId?: number | null;
  operatorId?: number | null;
  operatorUnassigned?: boolean;
}

function assigneeParams(params: HttpParams, filter?: DealAssigneeFilter): HttpParams {
  if (filter?.managerId) params = params.set('managerId', String(filter.managerId));
  if (filter?.operatorId) params = params.set('operatorId', String(filter.operatorId));
  if (filter?.operatorUnassigned) params = params.set('operatorUnassigned', 'true');
  return params;
}

export interface CargoDeal {
  id: number;
  /** Менеджер сделки на стороне вашей компании. */
  managerId?: number;
  managerName?: string;
  /** Оператор, закреплённый за сделкой на стороне вашей компании. */
  operatorId?: number;
  operatorName?: string;
  cargoRequestId: number;
  cargoOwnerId: number;
  cargoOwnerCompanyName: string;
  freightForwarderId: number;
  freightForwarderCompanyName: string;
  bidId: number;
  status: string;
  activities?: DealActivity[];
  createdAt: string;
  updatedAt?: string;
}

export interface CargoDealDetail {
  id: number;
  operatorId?: number;
  operatorName?: string;
  status: string;
  createdAt: string;
  updatedAt?: string;
  cargoOwner: ParticipantInfo;
  freightForwarder: ParticipantInfo;
  cargoRequest: CargoRequestDetail;
  bid: FreightForwarderBidDto;
  activities?: DealActivity[];
}

export interface CreateDealActivityRequest {
  activityTypeCode: string;
  targetRoleId?: number;
  actorMessage?: string;
  request?: boolean;
}

export interface CompleteDealActivityRequest {
  targetMessage?: string;
}

export interface DealReportTimelineItem {
  stageRu?: string;
  stageEn?: string;
  dateTime?: string;
  commentRu?: string;
  commentEn?: string;
}

export interface DealReportContent {
  titleRu?: string;
  titleEn?: string;
  generalInfoRu?: string;
  generalInfoEn?: string;
  timeline?: DealReportTimelineItem[];
  prosRu?: string[];
  prosEn?: string[];
  consRu?: string[];
  consEn?: string[];
  rating?: number;
  conclusionRu?: string;
  conclusionEn?: string;
}

/** ИИ-отчёт по сделке (оценка работы экспедитора). */
export interface DealReportDto {
  id: number;
  dealId: number;
  content: DealReportContent;
  /** Оценка ИИ по шкале до 5 */
  rating?: number;
  createdAt: string;
}

/** Статус асинхронной генерации отчёта — приходит через WebSocket /topic/deal-report/{dealId}. */
export interface DealReportStatus {
  dealId: number;
  /** PROCESSING | DONE | ERROR */
  status: 'PROCESSING' | 'DONE' | 'ERROR' | string;
  message?: string;
  error?: string;
  /** Заполнено только при status = DONE */
  report?: DealReportDto;
}

@Injectable({
  providedIn: 'root'
})
export class DealService {
  constructor(private http: HttpClient) {}

  /**
   * Принять ставку и создать сделку (только для грузовладельца)
   */
  acceptBid(bidId: number): Observable<CargoDeal> {
    return this.http.post<CargoDeal>(`${API_URL}/accept-bid/${bidId}`, {});
  }

  /**
   * Получить базовую информацию о сделке
   */
  getDeal(dealId: number): Observable<CargoDeal> {
    return this.http.get<CargoDeal>(`${API_URL}/${dealId}`);
  }

  /**
   * Получить детальную информацию о сделке
   */
  getDealDetail(dealId: number): Observable<CargoDealDetail> {
    return this.http.get<CargoDealDetail>(`${API_URL}/${dealId}/detail`);
  }

  /**
   * Получить сделку с активностями
   */
  getDealWithActivities(dealId: number): Observable<CargoDeal> {
    return this.http.get<CargoDeal>(`${API_URL}/${dealId}/activities`);
  }

  /**
   * Получить все сделки текущего грузовладельца
   */
  getMyDeals(): Observable<CargoDeal[]> {
    return this.http.get<CargoDeal[]>(`${API_URL}/my-deals`);
  }

  /**
   * Получить все сделки текущего грузовладельца с пагинацией
   */
  getMyDealsPaginated(page: number = 0, size: number = 20, filter?: DealAssigneeFilter): Observable<PageResponse<CargoDeal>> {
    let httpParams = assigneeParams(new HttpParams(), filter);
    httpParams = httpParams.set('page', page.toString());
    httpParams = httpParams.set('size', size.toString());
    httpParams = httpParams.set('sort', 'id,desc');
    return this.http.get<PageResponse<CargoDeal>>(`${API_URL}/my-deals`, { params: httpParams });
  }

  /**
   * Получить все сделки текущего экспедитора
   */
  getMyDealsForwarder(): Observable<CargoDeal[]> {
    return this.http.get<CargoDeal[]>(`${API_URL}/my-deals-forwarder`);
  }

  /**
   * Получить все сделки текущего экспедитора с пагинацией
   */
  getMyDealsForwarderPaginated(page: number = 0, size: number = 20, filter?: DealAssigneeFilter): Observable<PageResponse<CargoDeal>> {
    let httpParams = assigneeParams(new HttpParams(), filter);
    httpParams = httpParams.set('page', page.toString());
    httpParams = httpParams.set('size', size.toString());
    httpParams = httpParams.set('sort', 'id,desc');
    return this.http.get<PageResponse<CargoDeal>>(`${API_URL}/my-deals-forwarder`, { params: httpParams });
  }

  /**
   * Получить все сделки для таможенного брокера (с пагинацией)
   */
  getBrokerDealsPaginated(page: number = 0, size: number = 20): Observable<PageResponse<CargoDeal>> {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('page', page.toString());
    httpParams = httpParams.set('size', size.toString());
    httpParams = httpParams.set('sort', 'id,desc');
    return this.http.get<PageResponse<CargoDeal>>(`${API_URL}/broker-deals`, { params: httpParams });
  }

  /**
   * Добавить активность к сделке
   */
  addActivity(dealId: number, request: CreateDealActivityRequest): Observable<DealActivity> {
    return this.http.post<DealActivity>(`${API_URL}/${dealId}/activities`, request);
  }

  /**
   * Завершить активность (ответить на запрос)
   */
  completeActivity(activityId: number, request: CompleteDealActivityRequest): Observable<DealActivity> {
    return this.http.post<DealActivity>(`${API_URL}/activities/${activityId}/complete`, request);
  }

  /**
   * Получить последний ИИ-отчёт по сделке (только для грузовладельца).
   * При отсутствии отчёта бэкенд возвращает 204 No Content — в ответ приходит null.
   */
  getReport(dealId: number): Observable<DealReportDto | null> {
    return this.http.get<DealReportDto>(`${API_URL}/${dealId}/report`, { responseType: 'json' });
  }

  /**
   * Запустить формирование ИИ-отчёта по сделке (только для грузовладельца). Генерация идёт
   * асинхронно на бэкенде — метод только стартует её (202 Accepted); прогресс и результат
   * нужно слушать через DealReportSocketService.watch(dealId) (с поллингом getReport как фолбэк).
   */
  generateReport(dealId: number): Observable<void> {
    return this.http.post<void>(`${API_URL}/${dealId}/report/generate`, {});
  }
}
